import { useCallback, useMemo } from 'react';
import { AdviseSupplierScoring, SupplierRating } from '../../../types/advise';
import { CallResult } from '../../../Api/callExternalApi';
import { fetchAdviseSupplierScoring, updateAdviseSupplierRating } from '../../../Api/Advise/SupplierScoring';
import { EngagementSupplierScoring } from '../../Compounds/Advise/SideBySideView';
import { addError } from '../../../store/Error';
import { usePendingSaves } from './usePendingSaves';
import { useLoadAfterSaves } from './useLoadAfterSaves';

type PendingSaves = ReturnType<typeof usePendingSaves>;

// The error upstack_api's Engagements::SupplierScoring gives a rating off the client average without a note.
const NOTE_REQUIRED = 'note_required';

const isNoteRequired = (error: CallResult<SupplierRating>['error']): boolean =>
  (error?.errorResponse?.response?.data as { error?: string } | undefined)?.error === NOTE_REQUIRED;
export const CLIENT_SCORES_CHANGED =
  'A client sent scores since this page loaded, so that rating now differs from the client average. Rate it again ' +
  'to add a note.';

const sameFunction = (a: SupplierRating, b: SupplierRating) =>
  a.supplier_id === b.supplier_id && a.function_id === b.function_id;

const withRating = (scoring: AdviseSupplierScoring, saved: SupplierRating): AdviseSupplierScoring => ({
  ...scoring,
  ratings: [...scoring.ratings.filter((row) => !sameFunction(row, saved)), saved]
});

// Who wrote a note, and when, comes back with the save; taken only while the rating still shows what was sent.
const withSavedNote = (scoring: AdviseSupplierScoring, saved: SupplierRating): AdviseSupplierScoring => {
  const shown = scoring.ratings.find((row) => sameFunction(row, saved));
  return shown && shown.rating === saved.rating && shown.note === saved.note ? withRating(scoring, saved) : scoring;
};

/** UPSTACK's function ratings of the shortlist, loaded when the tab asks, with each change saved in the background. */
export const useEngagementSupplierScoring = (
  engagementId: string | null,
  canEdit: boolean,
  { debouncedSave, flushSaves }: PendingSaves
): EngagementSupplierScoring => {
  const {
    data: scoring,
    failed,
    refresh,
    showEdit,
    saveOrReload
  } = useLoadAfterSaves(engagementId, fetchAdviseSupplierScoring, flushSaves);

  // A supplier dropped from the shortlist since answers 404, so its save is dropped and the tab reloaded.
  const rate = useCallback(
    (supplierId: string, functionId: string, rating: number, note: string | null = null) => {
      if (!canEdit || !engagementId) return;
      const shown = {
        supplier_id: supplierId,
        function_id: functionId,
        rating,
        note,
        noted_by_name: null,
        noted_at: null
      };
      showEdit((prev) => withRating(prev, shown));
      debouncedSave(
        `supplierRating:${engagementId}:${supplierId}:${functionId}`,
        saveOrReload(async () => {
          const result = await updateAdviseSupplierRating(engagementId, supplierId, functionId, rating, note);
          // Judged against the client average this page loaded; a newer score moved it, so reload rather than retry.
          if (isNoteRequired(result.error)) {
            refresh();
            addError('advise-engagement', CLIENT_SCORES_CHANGED);
            return { error: null };
          }
          const { data } = result;
          if (data) showEdit((prev) => withSavedNote(prev, data));
          return result;
        })
      );
    },
    [canEdit, engagementId, debouncedSave, saveOrReload, showEdit, refresh]
  );

  return useMemo(() => ({ scoring, failed, refresh, rate }), [scoring, failed, refresh, rate]);
};
